import { CommonModule } from '@angular/common';
import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import {
  AdminNavConfig,
  AdminNavItem,
  getAdminNavConfig,
} from '../../../config/admin-nav.config';
import { NotificationsPanelComponent } from '../../shared/notifications-panel/notifications-panel.component';
import { DeskChatPanelComponent } from '../../shared/desk-chat/desk-chat-panel.component';
import { NotificationsService } from '../../../service/admin/notifications.service';

@Component({
  selector: 'app-admin-shell',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, NotificationsPanelComponent, DeskChatPanelComponent],
  templateUrl: './admin-shell.component.html',
  styleUrl: './admin-shell.component.scss',
})
export class AdminShellComponent implements OnInit, OnDestroy {
  navConfig: AdminNavConfig | null = null;
  userName = 'User';
  userRoleLabel = '';
  userInitials = 'U';

  openMenuKey: string | null = null;
  mobileOpen = false;
  notificationsOpen = false;
  deskChatOpen = false;
  showDeskChat = false;
  currentPath = '';

  private readonly subs = new Subscription();

  constructor(
    private router: Router,
    private notifications: NotificationsService,
  ) {}

  ngOnInit(): void {
    this.hydrateUser();
    this.notifications.start();
    this.currentPath = this.router.url.split('?')[0];
    this.subs.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => {
          this.currentPath = event.urlAfterRedirects.split('?')[0];
          this.closeAllOverlays();
          this.mobileOpen = false;
        }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.notifications.stop();
  }

  get menus(): AdminNavItem[] {
    return this.navConfig?.menus ?? [];
  }

  menuKey(menu: AdminNavItem, index: number): string {
    return `${menu.title}-${index}`;
  }

  isMenuOpen(menu: AdminNavItem, index: number): boolean {
    return this.openMenuKey === this.menuKey(menu, index);
  }

  toggleMenu(menu: AdminNavItem, index: number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!menu.items?.length) {
      return;
    }
    const key = this.menuKey(menu, index);
    this.openMenuKey = this.openMenuKey === key ? null : key;
    this.notificationsOpen = false;
    this.deskChatOpen = false;
  }

  isItemActive(item: AdminNavItem): boolean {
    if (item.path) {
      return this.currentPath === item.path || this.currentPath.startsWith(`${item.path}/`);
    }
    return !!item.items?.some((child) => this.isItemActive(child));
  }

  toggleMobile(): void {
    this.mobileOpen = !this.mobileOpen;
    this.closeAllOverlays();
  }

  onNotificationsOpenChange(open: boolean): void {
    this.notificationsOpen = open;
    if (open) {
      this.openMenuKey = null;
      this.deskChatOpen = false;
    }
  }

  onDeskChatOpenChange(open: boolean): void {
    this.deskChatOpen = open;
    if (open) {
      this.openMenuKey = null;
      this.notificationsOpen = false;
    }
  }

  closeAllOverlays(): void {
    this.openMenuKey = null;
    this.notificationsOpen = false;
    this.deskChatOpen = false;
  }

  logout(): void {
    this.router.navigateByUrl('/sso/admlogout');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target || !(target instanceof Element)) {
      this.closeAllOverlays();
      return;
    }

    const insideOpenMenu = !!target.closest('.admin-nav__item.is-open');
    const insideNotify = !!target.closest('.npanel, .ntrig.is-open, .admin-nav__notify.is-open');
    const insideChat = !!target.closest('.dpanel, .dtrig, .admin-nav__chat');
    const insideMobileToggle = !!target.closest('.admin-nav__burger');

    if (insideOpenMenu || insideNotify || insideChat || insideMobileToggle) {
      return;
    }

    this.closeAllOverlays();
  }

  get showDeskChatButton(): boolean {
    return this.showDeskChat;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeAllOverlays();
    this.mobileOpen = false;
  }

  @HostListener('document:keydown', ['$event'])
  onDeskChatShortcut(event: KeyboardEvent): void {
    if (!this.showDeskChatButton || event.repeat || event.isComposing) {
      return;
    }
    const alt = event.altKey;
    const ctrl = event.ctrlKey || event.metaKey;
    if (!alt || ctrl || event.shiftKey || event.code !== 'KeyD') {
      return;
    }
    event.preventDefault();
    this.onDeskChatOpenChange(!this.deskChatOpen);
  }

  private hydrateUser(): void {
    let user: any = {};
    try {
      user = JSON.parse(sessionStorage.getItem('loggedInUser') || '{}');
    } catch {
      user = {};
    }

    const userType = String(user?.type || '').toLowerCase();
    this.showDeskChat = userType === 'doctor' || userType === 'reception';
    this.navConfig = getAdminNavConfig(user?.type);
    const first = String(user?.first_name || user?.name || user?.full_name || '').trim();
    const last = String(user?.last_name || '').trim();
    const email = String(user?.email || '').trim();
    this.userName = [first, last].filter(Boolean).join(' ') || email || 'Clinic user';
    this.userRoleLabel = this.navConfig?.label || String(user?.type || 'Staff').replace(/_/g, ' ');
    this.userInitials = this.buildInitials(this.userName);
  }

  private buildInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return 'U';
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
}
